/**
 * Em que plataformas o app PODE pedir permissão de push.
 *
 * ## O risco que esta trava existe para impedir
 *
 * No iOS o token FCM depende do registro em APNs. Se a capability de Push
 * Notifications não estiver no App ID **e** nos entitlements do build, a
 * sequência é esta — e cada passo dela funciona:
 *
 *   1. o app pede permissão e o diálogo do sistema aparece;
 *   2. a pessoa **concede**;
 *   3. `Notification.permission` vira `"granted"`;
 *   4. o registro do token **falha em silêncio** — não há APNs por trás;
 *   5. o convite **nunca mais reaparece**, porque quem barra o convite é o
 *      próprio `Notification.permission` — ver `canOfferPushPrompt`.
 *
 * Resultado: a pessoa é contada como quem disse sim, e não recebe nada. Nunca.
 * O diálogo nativo é **one-shot por origem**, então a chance única daquele
 * aparelho foi gasta num "sim" que não produziu canal nenhum — e não existe
 * caminho, nem no app nem no sistema, para desfazer isso.
 *
 * Não há teste que pegue essa falha: todos os passos "passam". Por isso a
 * trava é de PLATAFORMA e não de código de erro.
 *
 * ## Por que uma constante local, e não config remota
 *
 * Config remota (Remote Config, doc de Firestore, env) pode chegar **vazia** —
 * projeto novo, regra que negou a leitura, cold start antes do fetch — e um
 * `valor ?? true` transforma ausência em liberação. A falha acima é
 * irreversível por pessoa; o default tem de ser seguro por construção, e um
 * literal no bundle é a única forma que não tem estado ausente.
 *
 * ## Como LIGAR (não é marcar esta flag)
 *
 * A flag sozinha não é prova. O caminho de liberação é: build nativo com a
 * capability, e **confirmar em device que `fetchFcmToken()` devolve token** —
 * não que o diálogo apareceu, não que a permissão ficou `granted`. Só o token
 * prova que existe canal. Depois disso, esta constante vira `true` no mesmo
 * commit que carrega o build validado.
 *
 * A rede de segurança de runtime existe em paralelo e é independente desta
 * flag: a permissão só é considerada CONSUMIDA quando há token registrado
 * (`canOfferPushPrompt`). Assim, mesmo que alguém ligue a flag cedo demais, o
 * estado "concedeu e não registrou" continua recuperável.
 *
 * Blueprint: `docs/systems/push-orchestration.md` §10.
 */

import { isNativeApp } from "../../../lib/platform/native";

/**
 * O build nativo já provou, em device, que o token FCM registra?
 *
 * ✅ `true` desde 21/08/2026 — **QA do fundador em device (TestFlight, build
 * 1.4)**: toggle de Ajustes ativado, token registrado e push de teste
 * entregue. É exatamente a prova que este docblock sempre exigiu; a flag vira
 * no fluxo combinado ("esta constante vira `true` no mesmo commit que carrega
 * o build validado" — o build validado é o da leva 8/1.4, e este commit sobe
 * na leva 9).
 *
 * ⚠️ A flag NÃO foi apagada de propósito: ela é o interruptor de ROLLBACK
 * (mesmo desenho da trava do Founder no IAP). Se um build futuro regredir o
 * canal — capability caindo do App ID, chave APNs revogada — voltar isto a
 * `false` desliga as duas superfícies de convite sem tocar em mais nada,
 * enquanto o toggle de Ajustes continua como caminho manual.
 */
export const NATIVE_PUSH_PROMPT_READY = true;

/**
 * Pode pedir permissão de push nesta plataforma?
 *
 * Web: sim — é onde o caminho está provado hoje (SW + VAPID, medido em
 * produção). Nativo: só com `NATIVE_PUSH_PROMPT_READY`.
 *
 * ⚠️ NÃO é a mesma pergunta que `isFcmSupported()`. Aquela responde "este
 * runtime tem a API"; esta responde "pedir aqui é seguro". Hoje as duas
 * coincidem no nativo por acidente — `isFcmSupported` devolve `false` lá
 * porque **FCM Web** não vale no app empacotado. No dia em que o push nativo
 * entrar, `isFcmSupported` passará a dizer `true` e essa coincidência acaba;
 * é exatamente aí que esta trava precisa continuar de pé.
 */
export function isPushPromptAllowedOnPlatform(): boolean {
  if (isNativeApp()) return NATIVE_PUSH_PROMPT_READY;
  return true;
}
